// src/pages/providers/2_dashboard/utils/patientOverviewRoute.js
//
// Single source of truth for "open this patient's overview from the dashboard".
// Current and Archived tables both used to inline the same module → path map
// next to the View Patient button. The name-as-link work needs that map in two
// places per table (the name and the button), so it lives here once.
//
// The path follows the patient's assessment module, not a one-size-fits-all
// /patient/:id — autism and CYP ADHD have their own overview routes, and
// sending those rows to the adult ADHD page is a real mis-route.

/**
 * Overview path for a dashboard patient row.
 *
 * Adult ADHD (the default, including a missing assessmentModule) stays on the
 * historic /patient/:id route. Modular pathways have their own overview URL.
 * Returns null when there is no patientId — callers must not render a link
 * with an empty href.
 *
 * @param {{ patientId?: string, assessmentModule?: string }} patient
 * @returns {string|null}
 */
export function getPatientOverviewPath(patient) {
  const patientId = patient?.patientId;
  if (!patientId) return null;

  const module = patient.assessmentModule || 'adultAdhd';
  if (module === 'adultAutism') return `/patient/${patientId}/adult-autism`;
  if (module === 'cypAdhd') return `/patient/${patientId}/cyp-adhd`;
  return `/patient/${patientId}`;
}

/**
 * React Router location state that PatientRouteWrapper reads while the new
 * patient's document is still loading. Passing the name means the loading
 * screen can say "Loading Jane Smith…" instead of a generic message, and it
 * never writes into the patient model so it cannot leak into clinical views.
 *
 * @param {{ firstName?: string, lastName?: string }} patient
 * @returns {{ patientSummary: { firstName: string, lastName: string } }}
 */
export function getPatientOverviewNavState(patient) {
  return {
    patientSummary: {
      firstName: patient?.firstName || '',
      lastName: patient?.lastName || '',
    },
  };
}
